"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronDown } from "lucide-react";

import { motion, AnimatePresence } from "framer-motion";

const columns = [
  {
    title: "Privacy",
    links: ["Privacy Policy", "Cookie Policy", "Data Processing Agreement"],
  },
  {
    title: "Terms",
    links: ["Terms of Service", "Acceptable Use", "API Terms"],
  },
  {
    title: "Security",
    links: ["Security Overview", "Compliance", "Vulnerability Disclosure"],
  },
  {
    title: "Status",
    links: ["System Status", "Incident History"],
  },
];

function FooterAccordionItem({
  title,
  links,
}: {
  title: string;
  links: string[];
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-white/10 py-4">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between text-left text-base font-semibold text-white transition-colors hover:text-white/90"
        aria-expanded={open}
      >
        {title}
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
            className="overflow-hidden"
          >
            <ul className="pt-3 pb-1 space-y-3">
              {links.map((link, i) => (
                <li key={i}>
                  <a
                    href="#"
                    className="text-sm text-white/60 transition-colors hover:text-white"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#0F0F0F] py-12 text-white/70 sm:py-16">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Desktop / tablet layout */}
        <div className="hidden sm:flex sm:flex-row sm:justify-between sm:gap-12">
          <div className="max-w-xs">
            <Link href="/">
              <Image
                src="/logo.png"
                alt="MedTechData Logo"
                width={200}
                height={40}
                className="h-10 w-auto object-contain"
              />
            </Link>
            <p className="mt-6 text-sm leading-relaxed">
              Access 14.7M+ verified records across 500+ datasets. Integrate
              in minutes with our clean RESTful API. Scale from prototype to
              production with confidence.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
            {columns.map((col) => (
              <div key={col.title}>
                <p className="mb-4 text-sm font-semibold text-white">
                  {col.title}
                </p>
                <ul className="space-y-3">
                  {col.links.map((link, i) => (
                    <li key={i}>
                      <a
                        href="#"
                        className="text-sm transition-colors hover:text-white"
                      >
                        {link}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="hidden border-t border-white/10 pt-8 text-xs sm:mt-16 sm:block">
          © {year} Medtechdata Marketplace. All rights reserved.
        </div>

        {/* Mobile layout */}
        <div className="sm:hidden">
          <Link href="/">
            <Image
              src="/logo.png"
              alt="MedTechData Logo"
              width={200}
              height={40}
              className="h-9 w-auto object-contain"
            />
          </Link>
          <p className="mt-4 text-sm leading-relaxed">
            Access 14.7M+ verified records across 500+ datasets. Integrate in
            minutes with our clean RESTful API. Scale from prototype to
            production with confidence.
          </p>
          <p className="mt-6 text-xs text-white/50">
            © {year} Medtechdata Marketplace. All rights reserved.
          </p>

          <div className="mt-8 border-t border-white/10">
            {columns.map((col) => (
              <FooterAccordionItem
                key={col.title}
                title={col.title}
                links={col.links}
              />
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
